import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  analyzeMvx,
  indexSegments,
  MvxIndexResult,
  MvxInputs,
  MvxPattern,
  MvxPatternId,
  MvxZone,
  Specimen,
} from '../../models/MetabolicVulnerability';
import { MvxScoreGauge } from '../charts/MvxScoreGauge';
import { MUTED_PALETTE } from '../charts/chartPalette';

/**
 * Metabolic Vulnerability (MVX Plus) Insights panel. The interpretation layer:
 * the hero MVX gauge + a plain-language read of the score and the patterns it
 * points to. The marker bar graphs live in the sibling Results panel
 * (`MvxResultsPanel`); the step routing lives in `MvxBlueprintPanel`.
 *
 * Takes an `MvxInputs` bag off the bloodwork (markers aren't in `Bloodwork` yet).
 */
interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
  specimen?: Specimen;
}

/** Pull the nine MVX values off a bloodwork into the model's input shape. */
export function inputsFromBloodwork(ba?: BloodworkAnalysis): MvxInputs {
  const bw: any = ba && ba.bloodwork ? ba.bloodwork : {};
  return {
    mvx: bw.mvx, ivx: bw.ivx, mmx: bw.mmx,
    glycA: bw.glycA, sHdlP: bw.sHdlP, citrate: bw.citrate,
    valine: bw.valine, leucine: bw.leucine, isoleucine: bw.isoleucine,
  };
}

const GAUGE_PALETTE: Record<MvxZone, string> = MUTED_PALETTE;
const ZONE_LABEL: Record<MvxZone, string> = { green: 'Optimal', yellow: 'Borderline', orange: 'Elevated', red: 'High' };

const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100));

// Capitalize every word (incl. after hyphens) for the pattern bullets.
const titleCase = (s: string): string =>
  s.toLowerCase().replace(/(^|\s|-)(\w)/g, (_m, sep, c) => sep + c.toUpperCase());

/**
 * Client-facing read under the gauge. Leads with the overall MVX score, names
 * the driving sub-score(s) — inflammation (IVX) vs metabolic strain (MMX) — then
 * lists the specific patterns as bullets. Adapts to whichever patterns fire.
 */
const interpretation = (mvx: MvxIndexResult, patterns: MvxPattern[], hasFactors: boolean): React.ReactNode => {
  if (!hasFactors) {
    return 'An overall metabolic-resilience score. Add the six MVX Plus factor values to surface the patterns and Blueprint steps.';
  }
  const level = ZONE_LABEL[mvx.zone].toLowerCase();
  const scoreText = mvx.hasValue ? ` (${fmt(mvx.value)})` : '';
  const active = patterns.filter(p => p.id !== MvxPatternId.Optimal);
  if (active.length === 0) {
    return `The MVX score is ${level}${scoreText} — markers sit within their optimal ranges.`;
  }
  const ivxDriven = active.some(p => p.id === MvxPatternId.Inflammation || p.id === MvxPatternId.ToxicBurden);
  const mmxDriven = active.some(
    p => p.id === MvxPatternId.Mitochondrial || p.id === MvxPatternId.Malnutrition || p.id === MvxPatternId.CarbLoad,
  );
  let driver = '';
  if (ivxDriven && mmxDriven) driver = ', driven by inflammation (IVX) and metabolic strain (MMX)';
  else if (ivxDriven) driver = ', driven by inflammation (IVX)';
  else if (mmxDriven) driver = ', driven by metabolic strain (MMX)';
  return (
    <>
      <div style={{ fontWeight: 'bold' }}>{`The MVX score is ${level}${scoreText}${driver}. The markers show:`}</div>
      <ul style={{ listStyle: 'none', paddingLeft: 0, marginLeft: 0, marginTop: 4, marginBottom: 0 }}>
        {active.map((p, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', color: '#337ab7', fontWeight: 'bold', marginBottom: 2 }}>
            <span style={{ display: 'inline-block', marginRight: 4 }}>•</span>
            <span>{titleCase(p.clientTitle)}</span>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 6 }}>See the 7-Step Blueprint for where to focus.</div>
    </>
  );
};

export class MetabolicVulnerabilityInsights extends React.Component<Props> {
  static shouldRender(ba: BloodworkAnalysis | undefined): boolean {
    return analyzeMvx(inputsFromBloodwork(ba)).anyEntered;
  }

  // MVX hero gauge — the same Highcharts solid-gauge as the Glucose gauge, with
  // the zone bands from the (specimen-specific) decision limit. The black
  // "Metabolic Vulnerability is" line + blue "{level} ({score})" render inside it.
  renderHero(mvx: MvxIndexResult) {
    if (!mvx.hasValue) {
      return (
        <div style={{ textAlign: 'center', color: '#999', fontSize: 12, padding: '18px 0' }}>
          Enter the MVX score to show the vulnerability gauge.
        </div>
      );
    }
    // The MVX score can exceed 100 — scale the gauge (and its red band) up past
    // 100 so the needle shows the true value instead of pegging at the edge.
    const gaugeMax = Math.max(100, Math.ceil(mvx.value / 10) * 10);
    const segs = indexSegments(mvx, this.props.specimen, gaugeMax);
    const plotBands = segs.map(s => ({ color: GAUGE_PALETTE[s.zone], from: s.from, to: s.to }));
    // Label 0 and the three zone boundaries (green/yellow · decision limit · orange/red);
    // not the top endpoint — the scale can run past 100, so the top stays unlabeled.
    const ticks = Array.from(new Set([0, ...segs.slice(0, -1).map(s => s.to)])).sort((a, b) => a - b);
    return (
      <MvxScoreGauge
        value={mvx.value}
        text="Metabolic Vulnerability is"
        category={ZONE_LABEL[mvx.zone]}
        plotBands={plotBands}
        ticks={ticks}
        max={gaugeMax}
        chartContainerId="mvxVulnerabilityGauge"
      />
    );
  }

  // The two sub-scores (IVX / MMX) explained, under the MVX gauge.
  renderSubScores(indices: MvxIndexResult[]) {
    const subs = indices.filter(i => i.id !== 'mvx');
    return (
      <div style={{ marginTop: 12, padding: '10px 8px 0', borderTop: '1px solid #eee7df', textAlign: 'left' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#2a2a2a', marginBottom: 8 }}>
          The MVX score combines two sub-scores:
        </div>
        {subs.map(ix => (
          <div key={ix.id} style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: '#2a2a2a' }}>
              {ix.short} · {ix.label}
            </div>
            <div style={{ fontSize: 10.5, color: '#857d73', lineHeight: 1.4, marginTop: 1 }}>{ix.client}</div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis, specimen } = this.props;
    const result = analyzeMvx(inputsFromBloodwork(bloodworkAnalysis), specimen);
    if (!result.anyEntered) return null;

    const mvx = result.indices.find(i => i.id === 'mvx')!;
    const hasFactors = result.factors.some(f => f.hasValue);

    return (
      <Card className="evaluationPanel" id="metabolic-vulnerability-insights">
        <Card.Header className="evaluationPanelHeading">
          <div>MVX Plus Insights</div>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {this.renderHero(mvx)}
            <div style={{ fontSize: 12, color: '#4a443e', lineHeight: 1.5, padding: '10px 8px 0', textAlign: 'left' }}>
              {interpretation(mvx, result.patterns, hasFactors)}
            </div>
            {this.renderSubScores(result.indices)}
            <div style={{ textAlign: 'center', fontSize: 10.5, color: '#999', marginTop: 10 }}>
              A near-term metabolic-resilience score.
              <br />
              Observational — not diagnostic.
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default MetabolicVulnerabilityInsights;
